import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import { FaStar, FaQuoteLeft } from 'react-icons/fa';
import { useTheme } from '../../../context/ThemeContext';

import Rajamohan from './images/rajamohan.jpg';
import Arvind from './images/ArvindBatra.jpeg';
import leonardo from './images/leonardo.jpeg';
import Mark from './images/mark.jpg';
import sachin from './images/sachin_gujar.jpg';

interface TestimonialData {
  id: number;
  name: string;
  position: string;
  content: string;
  image: string;
  review: number;
}

const defaultSliders: TestimonialData[] = [
  {
    id: 1,
    name: 'Rajamohan Dhandapani',
    position: 'Chair - RMB International',
    content: 'Rotary Means Business Jaipur has been a game-changer for my business. The networking opportunities are incredible, and I have built strong connections that have directly led to new clients and collaborations.',
    image: Rajamohan,
    review: 5
  },
  {
    id: 2,
    name: 'Arvind Kumar Batra',
    position: 'Vice Chair - RMB Jaipur',
    content: 'The monthly meetups are well-structured, and the members are truly supportive. It is an excellent platform for anyone looking to grow their business while building lasting relationships.',
    image: Arvind,
    review: 5
  },
  {
    id: 3,
    name: 'Leonardo Santos',
    position: 'Secretary - RMB International',
    content: 'I have gained so much from Rotary Means Business Jaipur. The events provide valuable insights into business strategies, and the network has helped me take my business to the next level.',
    image: leonardo,
    review: 5
  },
  {
    id: 4,
    name: 'Mark Burchill',
    position: 'Chair Emeritus & Treasurer',
    content: 'Joining Rotary Means Business Jaipur was one of the best decisions I have made. The community is welcoming, and I have seen a significant boost in my business through the connections I have made.',
    image: Mark,
    review: 5
  },
  {
    id: 5,
    name: 'Sachin Gururaj',
    position: 'Chair Emeritus & Immediate Past Chair',
    content: 'The platform fosters a professional yet friendly environment where business owners can thrive. I have enjoyed the discussions, and the networking has been truly beneficial for my growth.',
    image: sachin,
    review: 5
  }
];

const Testomonail = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [testimonialdata, setTestimonialdata] = useState<TestimonialData[]>(defaultSliders);
  const [activeslider, setSlider] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get<TestimonialData[]>(
          "https://rmbbackend.rmbjaipur.com/auth/testimonial"
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
          const transformedSliders = response.data.map((slider) => ({
            ...slider,
            image: slider.image
              ? (slider.image.startsWith('http')
                ? slider.image
                : `https://rmbbackend.rmbjaipur.com/${slider.image.replace(/\\/g, '/')}`)
              : Rajamohan,
            review: Number(slider.review) || 5,
          }));
          setTestimonialdata(transformedSliders);
        }
      } catch {
        // Fallback to defaultSliders on error
      }
    };

    fetchData();
  }, []);

  // Auto-scroll testimonials every 4 seconds
  useEffect(() => {
    if (testimonialdata.length === 0) return;
    const timer = setInterval(() => {
      setSlider((prev) => (prev + 1) % testimonialdata.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [testimonialdata]);

  const total = testimonialdata.length;
  const currentItem = testimonialdata[activeslider % (total || 1)];

  const handlePrevious = () => {
    setSlider((prevIndex) => (prevIndex === 0 ? total - 1 : prevIndex - 1));
  };

  const handleNext = () => {
    setSlider((prevIndex) => (prevIndex + 1) % total);
  };

  if (!currentItem) return null;

  return (
    <section className={`py-20 px-4 sm:px-8 lg:px-[8%] ${isLight ? 'bg-slate-100/90 text-slate-900' : 'bg-[#060D1A] text-slate-100'} transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto">

        {/* Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <span className={`inline-flex items-center gap-1.5 ${isLight ? 'bg-amber-500/10 text-amber-700 border-amber-500/30' : 'bg-[#FDBC12]/10 text-[#FDBC12] border-[#FDBC12]/25'} text-[11px] font-black uppercase px-3.5 py-1 rounded-full border tracking-[0.12em] mb-3`}>
              💬 Testimonials
            </span>
            <h2 className={`text-[32px] md:text-[42px] font-black ${isLight ? 'text-[#0A2451]' : 'text-white'} leading-tight`}>
              What Our <span className="text-[#FDBC12]">Leaders Say</span>
            </h2>
            <p className={`${isLight ? 'text-slate-600' : 'text-slate-400'} text-sm mt-2 max-w-lg`}>
              Real experiences and reviews from active Rotarians in our fellowship.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevious}
              className={`w-10 h-10 rounded-full border ${isLight ? 'border-slate-300 hover:bg-slate-200 text-slate-700' : 'border-slate-700 hover:border-[#FDBC12] hover:bg-[#FDBC12]/10 text-slate-300 hover:text-[#FDBC12]'} flex items-center justify-center transition cursor-pointer`}
              title="Previous"
            >
              <RiArrowLeftSLine size={20} />
            </button>
            <button
              onClick={handleNext}
              className={`w-10 h-10 rounded-full border ${isLight ? 'border-slate-300 hover:bg-slate-200 text-slate-700' : 'border-slate-700 hover:border-[#FDBC12] hover:bg-[#FDBC12]/10 text-slate-300 hover:text-[#FDBC12]'} flex items-center justify-center transition cursor-pointer`}
              title="Next"
            >
              <RiArrowRightSLine size={20} />
            </button>
          </div>
        </div>

        {/* Centered Compact Card */}
        <div className="max-w-3xl mx-auto relative">
          <div className={`${isLight ? 'bg-white border-slate-200 shadow-2xl text-slate-900' : 'bg-gradient-to-b from-[#0F1D30] to-[#0A1527] border-slate-800 text-white'} border rounded-3xl p-8 sm:p-12 relative overflow-hidden transition-all duration-300`}>
            {/* Ambient Background Watermark Quote */}
            <FaQuoteLeft className={`absolute -top-4 -right-4 ${isLight ? 'text-slate-200/50' : 'text-slate-800/20'} text-9xl pointer-events-none select-none`} />

            <div className="relative z-10 flex flex-col items-center text-center space-y-6">
              {/* Member Avatar */}
              <div className="relative w-24 h-24 rounded-full p-1 bg-gradient-to-tr from-[#FDBC12] to-amber-500/20 shadow-2xl">
                <img
                  className="w-full h-full rounded-full object-cover bg-slate-900"
                  src={currentItem.image}
                  alt={currentItem.name}
                  onError={(e) => { e.currentTarget.src = Rajamohan; }}
                />
              </div>

              {/* Star Rating */}
              <div className="flex items-center gap-1 text-amber-400 text-base">
                {[...Array(Math.min(5, Math.max(1, currentItem.review || 5)))].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>

              {/* Quote Content */}
              <blockquote className={`${isLight ? 'text-slate-700' : 'text-slate-200'} text-base sm:text-lg leading-relaxed italic font-medium max-w-2xl`}>
                "{currentItem.content}"
              </blockquote>

              {/* Member Details */}
              <div className="pt-2">
                <h3 className={`text-xl font-black ${isLight ? 'text-slate-900' : 'text-white'}`}>{currentItem.name}</h3>
                <p className="text-[#FDBC12] text-xs font-bold mt-0.5">{currentItem.position}</p>
              </div>

              {/* Navigation & Counter Footer */}
              <div className={`w-full pt-6 mt-4 border-t ${isLight ? 'border-slate-200' : 'border-slate-800/80'} flex items-center justify-between`}>
                <span className="text-xs text-slate-500 font-bold">
                  {activeslider + 1} of {total}
                </span>

                {/* Slider Dots */}
                <div className="flex items-center gap-2">
                  {testimonialdata.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSlider(idx)}
                      className={`h-2 rounded-full transition-all cursor-pointer ${activeslider === idx ? 'w-6 bg-[#FDBC12]' : 'w-2 bg-slate-700 hover:bg-slate-500'
                        }`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>

                {/* Nav Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrevious}
                    className="w-9 h-9 rounded-full bg-[#07111E] border border-slate-700 hover:border-[#FDBC12] text-slate-300 hover:text-[#FDBC12] flex items-center justify-center transition cursor-pointer"
                    aria-label="Previous testimonial"
                  >
                    <RiArrowLeftSLine size={20} />
                  </button>
                  <button
                    onClick={handleNext}
                    className="w-9 h-9 rounded-full bg-[#07111E] border border-slate-700 hover:border-[#FDBC12] text-slate-300 hover:text-[#FDBC12] flex items-center justify-center transition cursor-pointer"
                    aria-label="Next testimonial"
                  >
                    <RiArrowRightSLine size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testomonail;